import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ArrowDownTrayIcon, MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { badgeClass, ConfirmDialog, dangerButtonClass, DetailItem, Field, FormActions, formatNumber, getApiErrorMessage, inputClass, inputErrorClass, primaryButtonClass, secondaryButtonClass } from '../../components/ui/FormControls';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { clean, formatDate, formatMoment, labelOf, openButtonClass, ProductChoice, ProductPicker, TableFooter, thClass, today, useListState } from '../quality/partApprovalShared';

type Category = 'drawing' | 'specification' | 'work_instruction' | 'inspection_plan' | 'certificate' | 'customer_document' | 'vendor_document' | 'other';
type DocumentStatus = 'active' | 'obsolete';
type RevisionStatus = 'draft' | 'released' | 'superseded';
type Standing = 'released' | 'draft_waiting' | 'never_released';

interface DocumentRow {
  id: string;
  documentNumber: string;
  title: string;
  category: Category;
  status: DocumentStatus;
  productId: string | null;
  productName: string | null;
  sku: string | null;
  obsoleteReason: string | null;
  notes: string | null;
  currentRevisionId: string | null;
  currentRevision: string | null;
  currentEffectiveDate: string | null;
  revisionCount: number;
  draftCount: number;
  latestDraft: string | null;
}

interface Revision {
  id: string;
  revision: string;
  status: RevisionStatus;
  changeNote: string | null;
  changeReference: string | null;
  fileName: string;
  fileSize: number;
  createdAt: string;
  uploadedBy: string | null;
  releasedAt: string | null;
  releasedBy: string | null;
  effectiveDate: string | null;
  supersededAt: string | null;
}

interface DocumentDetail extends DocumentRow {
  revisions: Revision[];
}

interface DocumentPage {
  data: DocumentRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface Summary {
  active: number;
  drawings: number;
  obsolete: number;
  draftsWaiting: number;
  withoutRelease: number;
}

const CATEGORIES: Category[] = ['drawing', 'specification', 'work_instruction', 'inspection_plan', 'certificate', 'customer_document', 'vendor_document', 'other'];
const MAX_BYTES = 25 * 1024 * 1024;
const ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp,.tif,.tiff,.bmp,.dwg,.dxf,.step,.stp,.igs,.iges,.stl,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip';
const ALLOWED = new Set(ACCEPT.split(','));

const REVISION_TONE: Record<RevisionStatus, string> = {
  draft: 'bg-amber-50 text-amber-700 ring-amber-600/20',
  released: 'bg-green-50 text-green-700 ring-green-600/20',
  superseded: 'bg-gray-50 text-gray-600 ring-gray-500/20',
};
const REVISION_LABEL: Record<RevisionStatus, string> = { draft: 'Draft', released: 'In force', superseded: 'Superseded' };

const fileSize = (bytes: number) => (bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

// Why a chosen file cannot be sent, or nothing when it can
const fileProblem = (file: File | null): string | undefined => {
  if (!file) return 'Choose the file of this revision';
  const dot = file.name.lastIndexOf('.');
  const extension = dot === -1 ? '' : file.name.slice(dot).toLowerCase();
  if (!ALLOWED.has(extension)) return 'Use a PDF, a picture, a CAD file (DWG, DXF, STEP, IGES, STL), an office document or a ZIP';
  if (file.size === 0) return 'The file is empty';
  if (file.size > MAX_BYTES) return 'The file is larger than 25 MB';
  return undefined;
};

const REVISION_PATTERN = /^[A-Za-z0-9][A-Za-z0-9.-]{0,9}$/;
const NUMBER_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._/-]{1,49}$/;

const multipart = (fields: Record<string, string>, file: File) => {
  const form = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== '') form.append(key, value);
  });
  form.append('file', file);
  return form;
};

const sendForm = { headers: { 'Content-Type': 'multipart/form-data' } };

const downloadRevision = async (documentId: string, revision: Revision) => {
  try {
    const response = await api.get(`/documents/${documentId}/revisions/${revision.id}/download`, { responseType: 'blob' });
    const named = /filename="?([^";]+)"?/.exec(String(response.headers['content-disposition'] ?? ''));
    const url = URL.createObjectURL(response.data as Blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = named?.[1] ?? revision.fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch (error) {
    // A failed download comes back as a blob; its message is inside
    const data = (error as { response?: { data?: unknown } })?.response?.data;
    let message = 'The file could not be downloaded';
    if (data instanceof Blob) {
      try {
        message = JSON.parse(await data.text()).message || message;
      } catch {
        // keep the general message
      }
    }
    toast.error(message, { duration: 8000 });
  }
};

const FileInput: React.FC<{ id: string; file: File | null; onChange: (file: File | null) => void; error?: string }> = ({ id, file, onChange, error }) => (
  <Field label="File" htmlFor={id} required error={error} hint={file ? `${file.name} · ${fileSize(file.size)}` : 'PDF, picture, CAD file, office document or ZIP, up to 25 MB'}>
    <input
      id={id}
      type="file"
      accept={ACCEPT}
      onChange={(event) => onChange(event.target.files?.[0] ?? null)}
      className={cn('block w-full text-sm text-gray-700 file:mr-3 file:rounded-md file:border-0 file:bg-indigo-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-indigo-700 hover:file:bg-indigo-100', error && 'text-red-700')}
    />
  </Field>
);

const DocumentForm: React.FC<{ isPending: boolean; onCancel: () => void; onSubmit: (form: FormData) => void }> = ({ isPending, onCancel, onSubmit }) => {
  const [number, setNumber] = useState('');
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<Category>('drawing');
  const [product, setProduct] = useState<ProductChoice | null>(null);
  const [revision, setRevision] = useState('A');
  const [changeNote, setChangeNote] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [asked, setAsked] = useState(false);

  const errors = {
    number: number.trim() !== '' && !NUMBER_PATTERN.test(number.trim()) ? '2 to 50 letters, digits, dots, hyphens, underscores or slashes' : undefined,
    title: title.trim().length < 2 ? 'Give the document a title' : undefined,
    revision: !REVISION_PATTERN.test(revision.trim()) ? '1 to 10 letters, digits, dots or hyphens' : undefined,
    file: fileProblem(file),
  };
  const valid = !errors.number && !errors.title && !errors.revision && !errors.file;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setAsked(true);
    if (!valid || !file) return;
    onSubmit(multipart({ documentNumber: number.trim(), title: title.trim(), category, productId: product?.id ?? '', revision: revision.trim(), changeNote: changeNote.trim(), notes: notes.trim() }, file));
  };

  return (
    <form onSubmit={submit} noValidate className="space-y-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Document or drawing number" htmlFor="doc-number" error={asked ? errors.number : undefined} hint="Leave empty to take the next number of the year (DOC/26-27/0001)">
          <input id="doc-number" type="text" maxLength={50} value={number} onChange={(event) => setNumber(event.target.value.toUpperCase())} placeholder="DRG-1042" className={cn(inputClass, asked && errors.number && inputErrorClass)} />
        </Field>
        <Field label="Category" htmlFor="doc-category" required>
          <select id="doc-category" value={category} onChange={(event) => setCategory(event.target.value as Category)} className={inputClass}>
            {CATEGORIES.map((value) => (<option key={value} value={value}>{labelOf(value)}</option>))}
          </select>
        </Field>
        <Field label="Title" htmlFor="doc-title" required error={asked ? errors.title : undefined} className="sm:col-span-2">
          <input id="doc-title" type="text" maxLength={200} value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Bracket, mounting, 12 mm" className={cn(inputClass, asked && errors.title && inputErrorClass)} />
        </Field>
        <Field label="Part it describes" htmlFor="doc-product" hint="Optional" className="sm:col-span-2">
          <ProductPicker id="doc-product" value={product} onChange={setProduct} />
        </Field>
      </div>
      <div className="rounded-md bg-gray-50 p-4 ring-1 ring-gray-200">
        <h4 className="text-sm font-semibold text-gray-900">First revision</h4>
        <p className="mt-1 text-xs text-gray-500">It is saved as a draft. Nothing is in force until the revision is released.</p>
        <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label="Revision" htmlFor="doc-revision" required error={asked ? errors.revision : undefined}>
            <input id="doc-revision" type="text" maxLength={10} value={revision} onChange={(event) => setRevision(event.target.value.toUpperCase())} className={cn(inputClass, asked && errors.revision && inputErrorClass)} />
          </Field>
          <div className="sm:col-span-2">
            <FileInput id="doc-file" file={file} onChange={setFile} error={asked ? errors.file : undefined} />
          </div>
          <Field label="What this revision is" htmlFor="doc-change" className="sm:col-span-3">
            <input id="doc-change" type="text" maxLength={500} value={changeNote} onChange={(event) => setChangeNote(event.target.value)} placeholder="First issue" className={inputClass} />
          </Field>
        </div>
      </div>
      <Field label="Notes" htmlFor="doc-notes">
        <input id="doc-notes" type="text" maxLength={500} value={notes} onChange={(event) => setNotes(event.target.value)} className={inputClass} />
      </Field>
      <FormActions>
        <button type="button" onClick={onCancel} disabled={isPending} className={secondaryButtonClass}>Cancel</button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>{isPending ? 'Uploading…' : 'Add Document'}</button>
      </FormActions>
    </form>
  );
};

const DocumentPanel: React.FC<{ documentId: string; canEdit: boolean; canRelease: boolean; canDelete: boolean; onClose: () => void; onChanged: () => void }> = ({ documentId, canEdit, canRelease, canDelete, onClose, onChanged }) => {
  const queryClient = useQueryClient();
  const { data: doc, isLoading, error } = useQuery<DocumentDetail>({
    queryKey: ['documents', documentId],
    queryFn: async () => (await api.get(`/documents/${documentId}`)).data,
  });

  const [mode, setMode] = useState<'view' | 'revision' | 'edit' | 'obsolete'>('view');
  const [revision, setRevision] = useState('');
  const [changeNote, setChangeNote] = useState('');
  const [changeReference, setChangeReference] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [asked, setAsked] = useState(false);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<Category>('drawing');
  const [product, setProduct] = useState<ProductChoice | null>(null);
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [releasing, setReleasing] = useState<Revision | null>(null);
  const [effectiveDate, setEffectiveDate] = useState(today());
  const [withdrawing, setWithdrawing] = useState<Revision | null>(null);
  const [deleting, setDeleting] = useState(false);

  const done = (message: string) => (updated?: DocumentDetail) => {
    toast.success(message);
    if (updated) queryClient.setQueryData(['documents', documentId], updated);
    setMode('view');
    setAsked(false);
    setReleasing(null);
    setWithdrawing(null);
    onChanged();
  };
  const failed = (fallback: string) => (cause: unknown) => toast.error(getApiErrorMessage(cause, fallback), { duration: 8000 });

  const addRevision = useMutation({
    mutationFn: async (form: FormData) => (await api.post(`/documents/${documentId}/revisions`, form, sendForm)).data as DocumentDetail,
    onSuccess: done('Revision added as a draft'),
    onError: failed('Could not add the revision'),
  });
  const release = useMutation({
    mutationFn: async (target: Revision) => (await api.post(`/documents/${documentId}/revisions/${target.id}/release`, { effectiveDate })).data as DocumentDetail,
    onSuccess: done('Revision released'),
    onError: failed('Could not release the revision'),
  });
  const withdraw = useMutation({
    mutationFn: async (target: Revision) => (await api.delete(`/documents/${documentId}/revisions/${target.id}`)).data as DocumentDetail,
    onSuccess: done('Draft withdrawn'),
    onError: failed('Could not withdraw the draft'),
  });
  const save = useMutation({
    mutationFn: async () => (await api.put(`/documents/${documentId}`, { title: title.trim(), category, productId: product?.id ?? '', notes: notes.trim() })).data as DocumentDetail,
    onSuccess: done('Document updated'),
    onError: failed('Could not update the document'),
  });
  const obsolete = useMutation({
    mutationFn: async () => (await api.post(`/documents/${documentId}/obsolete`, { reason: reason.trim() })).data as DocumentDetail,
    onSuccess: done('Document marked obsolete'),
    onError: failed('Could not mark the document obsolete'),
  });
  const reactivate = useMutation({
    mutationFn: async () => (await api.post(`/documents/${documentId}/reactivate`)).data as DocumentDetail,
    onSuccess: done('Document is in use again'),
    onError: failed('Could not bring the document back into use'),
  });
  const remove = useMutation({
    mutationFn: async () => api.delete(`/documents/${documentId}`),
    onSuccess: () => {
      toast.success('Document deleted');
      onChanged();
      onClose();
    },
    onError: failed('Could not delete the document'),
  });

  if (isLoading) return <p className="py-8 text-center text-sm text-gray-500">Loading…</p>;
  if (error || !doc) return <p className="py-8 text-center text-sm text-red-600">{getApiErrorMessage(error, 'This document could not be loaded')}</p>;

  const neverReleased = doc.revisions.every((item) => item.status === 'draft');
  const revisionError = !REVISION_PATTERN.test(revision.trim())
    ? '1 to 10 letters, digits, dots or hyphens'
    : doc.revisions.some((item) => item.revision === revision.trim().toUpperCase())
      ? `There already is a revision ${revision.trim().toUpperCase()}`
      : undefined;
  const fileError = fileProblem(file);

  const openEdit = () => {
    setTitle(doc.title);
    setCategory(doc.category);
    setProduct(doc.productId ? { id: doc.productId, name: doc.productName ?? '', sku: doc.sku ?? '' } : null);
    setNotes(doc.notes ?? '');
    setAsked(false);
    setMode('edit');
  };
  const openRevision = () => {
    setRevision('');
    setChangeNote('');
    setChangeReference('');
    setFile(null);
    setAsked(false);
    setMode('revision');
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <span className={cn(badgeClass, 'ring-1 ring-inset', doc.status === 'obsolete' ? 'bg-red-50 text-red-700 ring-red-600/20' : 'bg-green-50 text-green-700 ring-green-600/20')}>{doc.status === 'obsolete' ? 'Obsolete' : 'In use'}</span>
        <span className="text-sm text-gray-700">{labelOf(doc.category)}</span>
        {doc.status === 'obsolete' && doc.obsoleteReason && <span className="text-sm text-red-700">{doc.obsoleteReason}</span>}
      </div>

      {mode === 'edit' ? (
        <form
          noValidate
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            setAsked(true);
            if (title.trim().length >= 2) save.mutate();
          }}
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Title" htmlFor="de-title" required error={asked && title.trim().length < 2 ? 'Give the document a title' : undefined} className="sm:col-span-2">
              <input id="de-title" type="text" maxLength={200} autoFocus value={title} onChange={(event) => setTitle(event.target.value)} className={cn(inputClass, asked && title.trim().length < 2 && inputErrorClass)} />
            </Field>
            <Field label="Category" htmlFor="de-category" required>
              <select id="de-category" value={category} onChange={(event) => setCategory(event.target.value as Category)} className={inputClass}>
                {CATEGORIES.map((value) => (<option key={value} value={value}>{labelOf(value)}</option>))}
              </select>
            </Field>
            <Field label="Part it describes" htmlFor="de-product">
              <ProductPicker id="de-product" value={product} onChange={setProduct} />
            </Field>
            <Field label="Notes" htmlFor="de-notes" className="sm:col-span-2">
              <input id="de-notes" type="text" maxLength={500} value={notes} onChange={(event) => setNotes(event.target.value)} className={inputClass} />
            </Field>
          </div>
          <FormActions>
            <button type="button" onClick={() => setMode('view')} disabled={save.isPending} className={secondaryButtonClass}>Cancel</button>
            <button type="submit" disabled={save.isPending} className={primaryButtonClass}>{save.isPending ? 'Saving…' : 'Save Changes'}</button>
          </FormActions>
        </form>
      ) : (
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <DetailItem label="Title" className="sm:col-span-2">{doc.title}</DetailItem>
          <DetailItem label="Revision in force">{doc.currentRevision ? `${doc.currentRevision}, from ${formatDate(doc.currentEffectiveDate)}` : 'None released yet'}</DetailItem>
          <DetailItem label="Part">{doc.productName ? `${doc.productName} (${doc.sku})` : '—'}</DetailItem>
          <DetailItem label="Notes" className="sm:col-span-2">{doc.notes || '—'}</DetailItem>
        </dl>
      )}

      <div>
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-900">Revisions</h4>
          {canEdit && doc.status === 'active' && mode === 'view' && (
            <button type="button" onClick={openRevision} className={secondaryButtonClass}>
              <PlusIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
              Add Revision
            </button>
          )}
        </div>

        {mode === 'revision' && (
          <form
            noValidate
            className="mt-3 space-y-4 rounded-md bg-gray-50 p-4 ring-1 ring-gray-200"
            onSubmit={(event) => {
              event.preventDefault();
              setAsked(true);
              if (revisionError || fileError || !file) return;
              addRevision.mutate(multipart({ revision: revision.trim(), changeNote: changeNote.trim(), changeReference: changeReference.trim() }, file));
            }}
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Field label="Revision" htmlFor="dr-revision" required error={asked ? revisionError : undefined}>
                <input id="dr-revision" type="text" maxLength={10} autoFocus value={revision} onChange={(event) => setRevision(event.target.value.toUpperCase())} className={cn(inputClass, asked && revisionError && inputErrorClass)} />
              </Field>
              <div className="sm:col-span-2">
                <FileInput id="dr-file" file={file} onChange={setFile} error={asked ? fileError : undefined} />
              </div>
              <Field label="What changed" htmlFor="dr-change" className="sm:col-span-2">
                <input id="dr-change" type="text" maxLength={500} value={changeNote} onChange={(event) => setChangeNote(event.target.value)} placeholder="Hole moved 2 mm" className={inputClass} />
              </Field>
              <Field label="Change reference" htmlFor="dr-reference" hint="Engineering change or letter">
                <input id="dr-reference" type="text" maxLength={100} value={changeReference} onChange={(event) => setChangeReference(event.target.value)} className={inputClass} />
              </Field>
            </div>
            <p className="text-xs text-gray-500">The revision is saved as a draft. The revision in force stays in force until this one is released.</p>
            <FormActions>
              <button type="button" onClick={() => setMode('view')} disabled={addRevision.isPending} className={secondaryButtonClass}>Cancel</button>
              <button type="submit" disabled={addRevision.isPending} className={primaryButtonClass}>{addRevision.isPending ? 'Uploading…' : 'Add Revision'}</button>
            </FormActions>
          </form>
        )}

        <div className="mt-3 overflow-x-auto rounded-md ring-1 ring-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className={thClass}>Revision</th>
                <th className={thClass}>File</th>
                <th className={thClass}>Uploaded</th>
                <th className={thClass}>Released</th>
                <th className={cn(thClass, 'text-right')}><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {doc.revisions.map((item) => (
                <tr key={item.id} data-testid="revision-row">
                  <td className="px-4 py-3 align-top">
                    <span className="font-medium text-gray-900">{item.revision}</span>
                    <span className={cn(badgeClass, 'ml-2 ring-1 ring-inset', REVISION_TONE[item.status])}>{REVISION_LABEL[item.status]}</span>
                    {item.changeNote && <span className="mt-1 block text-xs text-gray-600">{item.changeNote}</span>}
                    {item.changeReference && <span className="block text-xs text-gray-500">Ref. {item.changeReference}</span>}
                  </td>
                  <td className="px-4 py-3 align-top text-gray-700">
                    <span className="break-all">{item.fileName}</span>
                    <span className="block text-xs text-gray-500">{fileSize(item.fileSize)}</span>
                  </td>
                  <td className="px-4 py-3 align-top text-gray-700">
                    {formatMoment(item.createdAt)}
                    <span className="block text-xs text-gray-500">{item.uploadedBy || '—'}</span>
                  </td>
                  <td className="px-4 py-3 align-top text-gray-700">
                    {item.releasedAt ? (
                      <>
                        {formatMoment(item.releasedAt)}
                        <span className="block text-xs text-gray-500">{item.releasedBy || '—'} · in force from {formatDate(item.effectiveDate)}</span>
                        {item.supersededAt && <span className="block text-xs text-gray-500">Superseded {formatMoment(item.supersededAt)}</span>}
                      </>
                    ) : (
                      <span className="text-gray-400">Not released</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right align-top">
                    <div className="flex justify-end gap-2">
                      <button type="button" onClick={() => downloadRevision(doc.id, item)} title={`Download revision ${item.revision}`} aria-label={`Download revision ${item.revision}`} className={openButtonClass}>
                        <ArrowDownTrayIcon className="inline h-4 w-4" /> Download
                      </button>
                      {item.status === 'draft' && canRelease && doc.status === 'active' && (
                        <button type="button" onClick={() => { setEffectiveDate(today()); setReleasing(item); }} className={openButtonClass}>Release</button>
                      )}
                      {item.status === 'draft' && canEdit && doc.revisions.length > 1 && (
                        <button type="button" onClick={() => setWithdrawing(item)} className="rounded-md px-2.5 py-1 text-xs font-semibold text-red-600 ring-1 ring-inset ring-red-200 hover:bg-red-50">Withdraw</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {mode === 'obsolete' && (
        <form
          noValidate
          className="space-y-3 rounded-md bg-red-50 p-4 ring-1 ring-red-200"
          onSubmit={(event) => {
            event.preventDefault();
            setAsked(true);
            if (reason.trim().length >= 3) obsolete.mutate();
          }}
        >
          <Field label="Why is this document obsolete?" htmlFor="do-reason" required error={asked && reason.trim().length < 3 ? 'Say why the document is obsolete' : undefined}>
            <input id="do-reason" type="text" maxLength={300} autoFocus value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Part discontinued" className={inputClass} />
          </Field>
          <p className="text-xs text-red-700">Its revisions stay on record and can still be downloaded. No new revision can be added while it is obsolete.</p>
          <FormActions>
            <button type="button" onClick={() => setMode('view')} disabled={obsolete.isPending} className={secondaryButtonClass}>Cancel</button>
            <button type="submit" disabled={obsolete.isPending} className={dangerButtonClass}>{obsolete.isPending ? 'Saving…' : 'Mark Obsolete'}</button>
          </FormActions>
        </form>
      )}

      {mode === 'view' && (
        <div className="flex flex-wrap justify-end gap-3 border-t border-gray-200 pt-4">
          {canDelete && neverReleased && <button type="button" onClick={() => setDeleting(true)} className={dangerButtonClass}>Delete</button>}
          {canRelease && doc.status === 'active' && !neverReleased && <button type="button" onClick={() => { setReason(''); setAsked(false); setMode('obsolete'); }} className={secondaryButtonClass}>Mark Obsolete</button>}
          {canRelease && doc.status === 'obsolete' && <button type="button" onClick={() => reactivate.mutate()} disabled={reactivate.isPending} className={secondaryButtonClass}>Bring Back Into Use</button>}
          {canEdit && <button type="button" onClick={openEdit} className={secondaryButtonClass}>Edit</button>}
          <button type="button" onClick={onClose} className={secondaryButtonClass}>Close</button>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!releasing}
        onClose={() => setReleasing(null)}
        onConfirm={() => releasing && release.mutate(releasing)}
        title={`Release revision ${releasing?.revision ?? ''}`}
        message={
          <span className="block space-y-3">
            <span className="block">
              Revision {releasing?.revision} of {doc.documentNumber} becomes the one to work to.
              {doc.currentRevision ? ` Revision ${doc.currentRevision} is superseded.` : ''} A released revision cannot be changed or withdrawn.
            </span>
            <label className="block text-sm font-medium text-gray-700" htmlFor="release-date">
              In force from
              <input id="release-date" type="date" value={effectiveDate} onChange={(event) => setEffectiveDate(event.target.value)} className={cn(inputClass, 'mt-1')} />
            </label>
          </span>
        }
        confirmLabel="Release"
        pendingLabel="Releasing…"
        isPending={release.isPending}
        tone="primary"
      />
      <ConfirmDialog
        isOpen={!!withdrawing}
        onClose={() => setWithdrawing(null)}
        onConfirm={() => withdrawing && withdraw.mutate(withdrawing)}
        title={`Withdraw revision ${withdrawing?.revision ?? ''}`}
        message={`The draft revision ${withdrawing?.revision ?? ''} and its file ${withdrawing?.fileName ?? ''} are removed.`}
        confirmLabel="Withdraw"
        pendingLabel="Withdrawing…"
        isPending={withdraw.isPending}
        tone="danger"
      />
      <ConfirmDialog
        isOpen={deleting}
        onClose={() => setDeleting(false)}
        onConfirm={() => remove.mutate()}
        title={`Delete ${doc.documentNumber}`}
        message="The document was never released. It is removed together with its files."
        confirmLabel="Delete"
        pendingLabel="Deleting…"
        isPending={remove.isPending}
        tone="danger"
      />
    </div>
  );
};

const Documents: React.FC = () => {
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const canCreate = can('documents.create');
  const canEdit = can('documents.update');
  const canRelease = can('documents.approve');
  const canDelete = can('documents.delete');

  const { page, setPage, limit, setLimit, searchInput, setSearchInput, search } = useListState();
  const [category, setCategory] = useState<Category | ''>('');
  const [standing, setStanding] = useState<Standing | ''>('');
  const [status, setStatus] = useState<DocumentStatus | ''>('');
  const [adding, setAdding] = useState(false);
  const [viewingId, setViewingId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery<DocumentPage>({
    queryKey: ['documents', 'list', page, limit, search, category, standing, status],
    queryFn: async () => (await api.get('/documents', { params: clean({ page, limit, search, category, standing, status }) })).data,
    placeholderData: keepPreviousData,
  });
  const { data: summary } = useQuery<Summary>({ queryKey: ['documents', 'summary'], queryFn: async () => (await api.get('/documents/summary')).data });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['documents', 'list'] });
    queryClient.invalidateQueries({ queryKey: ['documents', 'summary'] });
  };

  const create = useMutation({
    mutationFn: async (form: FormData) => (await api.post('/documents', form, sendForm)).data as DocumentDetail,
    onSuccess: (created) => {
      toast.success(`${created.documentNumber} added. Release its revision to put it in force.`);
      setAdding(false);
      refresh();
      setViewingId(created.id);
    },
    onError: (cause) => toast.error(getApiErrorMessage(cause, 'Could not add the document'), { duration: 8000 }),
  });

  const rows = data?.data ?? [];
  const filtered = !!(search || category || standing || status);
  const viewing = rows.find((row) => row.id === viewingId);

  const stats = [
    { label: 'Documents in use', value: formatNumber(summary?.active ?? 0), hint: 'Not counting the obsolete ones' },
    { label: 'Drawings', value: formatNumber(summary?.drawings ?? 0), hint: 'Documents in use of the category Drawing' },
    { label: 'Drafts waiting for release', value: formatNumber(summary?.draftsWaiting ?? 0), hint: 'Revisions uploaded but not yet in force', alert: (summary?.draftsWaiting ?? 0) > 0 },
    { label: 'Obsolete', value: formatNumber(summary?.obsolete ?? 0), hint: 'Taken out of use; kept on record' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Documents &amp; Drawings</h1>
          <p className="mt-1 text-sm text-gray-500">Drawings, specifications and certificates with their revisions: which one is in force, what it replaced, and who released it.</p>
        </div>
        {canCreate && (
          <button type="button" onClick={() => setAdding(true)} className={primaryButtonClass}>
            <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Add Document
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className={cn('mt-1 text-2xl font-semibold', stat.alert ? 'text-amber-600' : 'text-gray-900')}>{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search by number, title, part, file name or change reference" aria-label="Search documents" className={cn(inputClass, 'pl-9')} />
          </div>
          <select value={category} onChange={(event) => { setCategory(event.target.value as Category | ''); setPage(1); }} aria-label="Category" className={cn(inputClass, 'lg:w-48')}>
            <option value="">All categories</option>
            {CATEGORIES.map((value) => (<option key={value} value={value}>{labelOf(value)}</option>))}
          </select>
          <select value={standing} onChange={(event) => { setStanding(event.target.value as Standing | ''); setPage(1); }} aria-label="Release" className={cn(inputClass, 'lg:w-52')}>
            <option value="">Released or not</option>
            <option value="released">With a revision in force</option>
            <option value="draft_waiting">With a draft waiting</option>
            <option value="never_released">Never released</option>
          </select>
          <select value={status} onChange={(event) => { setStatus(event.target.value as DocumentStatus | ''); setPage(1); }} aria-label="Status" className={cn(inputClass, 'lg:w-40')}>
            <option value="">In use and obsolete</option>
            <option value="active">In use</option>
            <option value="obsolete">Obsolete</option>
          </select>
        </div>

        {error ? (
          <p className="px-4 py-10 text-center text-sm text-red-600">{getApiErrorMessage(error, 'The documents could not be loaded')}</p>
        ) : isLoading ? (
          <p className="px-4 py-10 text-center text-sm text-gray-500">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <p className="text-sm font-medium text-gray-900">{filtered ? 'No documents match these filters' : 'No documents yet'}</p>
            <p className="mt-1 text-sm text-gray-500">{filtered ? 'Try another search or clear the filters.' : 'Add a drawing or a specification with its first revision to start.'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className={thClass}>Number</th>
                  <th className={thClass}>Title</th>
                  <th className={thClass}>Category</th>
                  <th className={thClass}>Part</th>
                  <th className={thClass}>Revision in force</th>
                  <th className={cn(thClass, 'text-right')}><span className="sr-only">Open</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {rows.map((row) => (
                  <tr key={row.id} className={cn('hover:bg-gray-50', row.status === 'obsolete' && 'bg-gray-50/60')} data-testid="document-row">
                    <td className="whitespace-nowrap px-4 py-3 text-sm">
                      <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">{row.documentNumber}</span>
                      {row.status === 'obsolete' && <span className={cn(badgeClass, 'ml-2 bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20')}>Obsolete</span>}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.title}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{labelOf(row.category)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.productName ? (<>{row.productName}<span className="block text-xs text-gray-500">{row.sku}</span></>) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.currentRevision ? (
                        <>
                          <span className="font-medium text-gray-900">Rev {row.currentRevision}</span>
                          <span className="block text-xs text-gray-500">from {formatDate(row.currentEffectiveDate)}</span>
                        </>
                      ) : (
                        <span className="text-gray-500">None released</span>
                      )}
                      {row.draftCount > 0 && <span className={cn(badgeClass, 'mt-1 bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-600/20')}>Draft {row.latestDraft} waiting</span>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <button type="button" onClick={() => setViewingId(row.id)} className={openButtonClass}>Open</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && rows.length > 0 && <TableFooter page={data.page} limit={limit} total={data.total} totalPages={data.totalPages} onPage={setPage} onLimit={setLimit} />}
      </div>

      <Modal isOpen={adding || !!viewingId} onClose={() => (adding ? !create.isPending && setAdding(false) : setViewingId(null))} title={adding ? 'Add Document' : viewing?.documentNumber ?? 'Document'} size="2xl">
        {adding ? (
          <DocumentForm isPending={create.isPending} onCancel={() => setAdding(false)} onSubmit={(form) => create.mutate(form)} />
        ) : (
          viewingId && <DocumentPanel key={viewingId} documentId={viewingId} canEdit={canEdit} canRelease={canRelease} canDelete={canDelete} onClose={() => setViewingId(null)} onChanged={refresh} />
        )}
      </Modal>
    </div>
  );
};

export default Documents;
